import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe2,
  Baby,
  Leaf,
  HandHeart,
  ArrowRight,
  HeartHandshake,
  Users,
  Sprout,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES, LOGO_URL } from "../data/site";
import SectionHeader from "../components/SectionHeader";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const impactIcons = [Globe2, Baby, Leaf, HandHeart];
const programIcons = [HeartHandshake, Baby, Leaf, Users];

export default function Home() {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroHome}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/65 to-[#0B5D1E]/85" />
        </div>

        <div className="relative gk-container py-24 text-white w-full">
          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger}
            className="max-w-4xl"
          >
            <motion.div
              variants={fadeUp}
              className="mb-10 flex flex-col sm:flex-row items-center sm:items-center gap-6 sm:gap-8"
              data-testid="hero-logo-badge"
            >
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-[#14803C] blur-3xl opacity-50" />
                <img
                  src={LOGO_URL}
                  alt="Grand Kasai Nsanga Lubangu Corporation logo"
                  className="relative h-44 w-44 sm:h-56 sm:w-56 lg:h-64 lg:w-64 rounded-full object-cover ring-8 ring-white/95 shadow-[0_20px_60px_-10px_rgba(20,128,60,0.6)]"
                />
              </div>
              <div className="text-center sm:text-left">
                <p className="font-heading font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Grand Kasaï
                </p>
                <p className="font-heading font-bold text-[#14803C] text-lg sm:text-xl lg:text-2xl mt-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  Nsanga Lubangu Corporation
                </p>
                <p className="font-body text-sm lg:text-base tracking-[0.3em] uppercase text-[#14803C] mt-2 font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  GKNL-Inc.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.25em] uppercase text-[#E8F1EA] mb-6">
              <span className="h-[2px] w-10 bg-[#14803C]" />
              {t.home.tagline}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-heading font-bold text-4xl sm:text-6xl lg:text-7xl leading-[1.02] text-white"
            >
              <span className="block">{t.home.heroTitle1}</span>
              <span className="block text-[#E8F1EA]">{t.home.heroTitle2}</span>
              <span className="block text-[#D48851]">{t.home.heroTitle3}</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 text-base sm:text-xl text-white/85 max-w-2xl leading-relaxed"
            >
              {t.home.heroSubtitle}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
              <Link to="/membership" data-testid="hero-join-btn" className="gk-btn-primary">
                {t.common.joinCommunity}
                <ArrowRight size={18} />
              </Link>
              <Link to="/get-involved" data-testid="hero-donate-btn" className="gk-btn-ghost-light">
                <HeartHandshake size={18} />
                {t.common.donateNow}
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.3em] uppercase">
          GKNL · Inc
        </div>
      </section>

      {/* IMPACT STRIP */}
      <section className="relative -mt-16 z-10" data-testid="impact-strip">
        <div className="gk-container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-white rounded-3xl p-4 shadow-[0_20px_60px_-30px_rgba(11,93,30,0.35)] border border-black/5"
          >
            {t.home.impactStrip.map((item, i) => {
              const Icon = impactIcons[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex flex-col p-6 rounded-2xl hover:bg-[#0B5D1E]/5 transition-colors"
                  data-testid={`impact-item-${i}`}
                >
                  <div className="h-12 w-12 rounded-2xl bg-[#0B5D1E] text-white flex items-center justify-center mb-4 shadow-lg shadow-[#0B5D1E]/25">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-neutral-900">{item.title}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 sm:py-32" data-testid="about-preview">
        <div className="gk-container grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="relative">
              <img
                src={IMAGES.community}
                alt="Community volunteers"
                className="w-full h-[520px] object-contain bg-[#0B5D1E]/5 rounded-[2rem] shadow-xl"
              />
              <div className="absolute -bottom-8 -right-4 sm:-right-8 bg-white rounded-3xl p-6 shadow-2xl border border-black/5 max-w-[260px]">
                <Sprout className="text-[#0B5D1E]" size={28} />
                <p className="mt-3 font-heading font-bold text-2xl leading-tight">100+</p>
                <p className="text-sm text-neutral-600 mt-1">Families supported across 3 continents</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <SectionHeader
              eyebrow={t.home.aboutEyebrow}
              title={t.home.aboutTitle}
              subtitle={t.home.aboutBody}
            />
            <div className="mt-8">
              <Link to="/about" data-testid="about-preview-cta" className="gk-btn-outline">
                {t.common.learnMore}
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-24 sm:py-32 bg-[#0B5D1E]/[0.03]" data-testid="programs-preview">
        <div className="gk-container">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <SectionHeader
              eyebrow={t.home.programsEyebrow}
              title={t.home.programsTitle}
            />
            <Link
              to="/programs"
              data-testid="home-view-programs"
              className="text-[#0B5D1E] font-semibold font-heading inline-flex items-center gap-2 hover:gap-3 transition-all"
            >
              {t.common.viewPrograms} <ArrowRight size={18} />
            </Link>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {t.home.programsItems.map((p, i) => {
              const Icon = programIcons[i];
              return (
                <motion.article
                  key={i}
                  variants={fadeUp}
                  className="gk-card group"
                  data-testid={`program-card-${i}`}
                >
                  <div className="h-14 w-14 rounded-2xl bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center mb-5 group-hover:bg-[#0B5D1E] group-hover:text-white transition-colors">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-neutral-900">{p.title}</h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{p.desc}</p>
                  <Link
                    to="/programs"
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0B5D1E] hover:gap-2 transition-all"
                  >
                    {t.common.learnMore} <ArrowRight size={14} />
                  </Link>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0">
          <img src={IMAGES.environment} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0A]/80" />
        </div>
        <div className="relative gk-container text-center max-w-3xl mx-auto text-white">
          <span className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.25em] uppercase text-[#E8F1EA] mb-5">
            <span className="h-[2px] w-10 bg-[#14803C]" />
            {t.home.ctaEyebrow}
            <span className="h-[2px] w-10 bg-[#14803C]" />
          </span>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {t.home.ctaTitle}
          </h2>
          <p className="mt-5 text-white/80 text-lg">{t.home.ctaSubtitle}</p>

          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/get-involved" data-testid="cta-volunteer-btn" className="gk-btn-primary">
              <Users size={18} />
              {t.common.volunteer}
            </Link>
            <Link to="/get-involved" data-testid="cta-donate-btn" className="gk-btn-secondary">
              <HeartHandshake size={18} />
              {t.common.donateNow}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
